import React from 'react'
import { connect } from 'react-redux'
import { Body, FlexWrapper } from 'components/Shell/Shell'
import FloatingMenu from 'components/FloatingMenu/FloatingMenu'
import { Spinner } from 'components/Loaders/Loaders'
import ICONS, { FORM_MANAGER_ICON_KEYS } from 'components/Icons/Icons'
import ImportDialog from './ImportDialog'
import FabButton, { FabSubButton } from '../Shell/FabButton'
import { DragProvider, DragState } from 'components/DragAndDrop/DragContext'
import Droppable from 'components/DragAndDrop/Droppable'
import CrossIcon from '@tecuity/sos-icons/react/cross-heavy'
import ImportIcon from '@tecuity/sos-icons/react/import'
import AddWizardIcon from '@tecuity/sos-icons/react/add-wizard'
import AddCategoryIcon from '@tecuity/sos-icons/react/add-category'
import Portal from 'components/Portal/Portal'
import {
  setAndPushIcon,
  setFilter,
  getAllWizards,
  fetchStructure,
  setActiveCategory,
  setCategoryTitle,
  toggleVisibility,
  reorderCategory,
  addCategory,
  deleteCategory,
  unpublishWizard,
} from './FormManagerActions'
import Category from './Category'
import { history } from '../../store'
import './FormManager.scss'
import { SUBROUTES } from '../../App'

let ConnectedFormManager = (props) => {
  const {
    match,
    wizards,
    categories,
    categoriesOrder,
    categoryIcons,
    activeCategory,
    getAllWizards,
    fetchStructure,
  } = props

  React.useEffect(() => {
    if (match.params.category) {
      if (
        !(SUBROUTES || []).find(
          (s) => s.DisplayInFormsManager && s.parameter === match.params.category
        )
      ) {
        // console.log('// category in URL does not exist in the Subroutes with DisplayInFormsManager')
        redirectToFirstSubroute()
      } else if (activeCategory !== match.params.category) {
        // console.log('// updating the activeCategory to what the URL was provided.')
        setActiveCategory(match.params.category)
        getAllWizards(match.params.category)
      }
    }
    if (activeCategory && !match.params.category) {
      // console.log('setting activeCategory into the URL')
      history.push(`/FormsManager/${activeCategory}`)
    }
    if (!activeCategory && !match.params.category) {
      // console.log('No active category, and not in URL, redirect to first')
      redirectToFirstSubroute()
    }
  }, [activeCategory, match.params.category])

  const redirectToFirstSubroute = () => {
    const firstSubroute = (SUBROUTES || [])
      .filter((s) => s.DisplayInFormsManager)
      .sort((a, b) => a.label.localeCompare(b.label))[0]
    if (firstSubroute) {
      history.push(`/FormsManager/${firstSubroute.parameter}`)
    } else {
      history.push('/')
    }
  }

  React.useEffect(() => {
    if (!categories) {
      fetchStructure()
    }
  }, [categories])

  const [orderedCategories, uncategorizedWizards] = React.useMemo(() => {
    if (
      categoriesOrder &&
      categories &&
      categoriesOrder[match.params.category] &&
      match.params.category &&
      wizards[match.params.category]
    ) {
      const ordered = categoriesOrder[match.params.category].map((id) => {
        return {
          ...categories[match.params.category][id],
          id,
          wizards: (
            (categories[match.params.category][id] &&
              categories[match.params.category][id].wizardsOrder) ||
            []
          )
            .map((wizardId) => wizards[match.params.category][wizardId])
            .filter((x) => x),
        }
      })
      const uncategorized = Object.values(wizards[match.params.category]).filter(
        (wizard) =>
          !ordered
            .flatMap((cat) => cat.wizards.map((w) => w.FORM_DEFINITION_ID))
            .includes(wizard.FORM_DEFINITION_ID)
      )
      return [ordered, uncategorized]
    } else {
      return [[], []]
    }
  }, [categoriesOrder, categories, match.params.category, wizards])

  if (!categories || !match.params.category || !wizards[match.params.category]) {
    return (
      <Body>
        <div className="form-manager forms loader-wrapper">
          <Spinner className="page-loader" themeColor="primary" />
        </div>
      </Body>
    )
  }

  return (
    <FormManager
      activeCategory={activeCategory}
      categories={orderedCategories}
      uncategorizedWizards={uncategorizedWizards}
      onRequestRefetch={() => getAllWizards(match.params.category)}
      icon={categoryIcons[match.params.category]}
    />
  )
}
export default connect(
  (state) => ({
    categories: state.formManager.categories,
    categoriesOrder: state.formManager.categoriesOrder,
    categoryIcons: state.formManager.categoryIcons,
    activeCategory: state.formManager.activeCategory,
    wizards: state.formManager.wizards,
  }),
  { getAllWizards, fetchStructure, setActiveCategory }
)(ConnectedFormManager)

let FormManager = ({
  filter,
  activeCategory,
  categories = [],
  uncategorizedWizards = [],
  icon,
  setCategoryTitle,
  toggleVisibility,
  reorderCategory,
  addCategory,
  deleteCategory,
  onRequestRefetch,
  unpublishWizard,
}) => {
  const [importDialogOpen, setImportDialogOpen] = React.useState(false)
  const scrollInterval = React.useRef()

  const startNewWizard = () => history.push(`edit/0?category=${activeCategory}`)

  const closeImportDialog = () => setImportDialogOpen(false)

  const closeAndReload = () => {
    closeImportDialog()
    onRequestRefetch()
  }

  const scrollUp = () => {
    if (window.scrollY > 0) {
      document.documentElement.scrollTop = window.scrollY - 15
      scrollInterval.current = requestAnimationFrame(scrollUp)
    } else {
      stopScroll()
    }
  }
  const scrollDown = () => {
    if (window.scrollY < document.body.clientHeight) {
      document.documentElement.scrollTop = window.scrollY + 15
      scrollInterval.current = requestAnimationFrame(scrollDown)
    } else {
      stopScroll()
    }
  }
  const registerScrollUp = (e) => {
    scrollInterval.current = requestAnimationFrame(scrollUp)
  }
  const registerScrollDown = (e) => {
    scrollInterval.current = requestAnimationFrame(scrollDown)
  }
  const stopScroll = () => {
    cancelAnimationFrame(scrollInterval.current)
  }

  return (
    <Body className="form-manager forms">
      <DragProvider>
        <Toolbar selectedIcon={icon} />
        <FlexWrapper direction="column">
          {categories.map((category, i) => (
            <Category
              {...category}
              filter={filter}
              icon={icon}
              onVisibilityChange={(on) => toggleVisibility({ on, categoryId: category.id })}
              onTitleChange={(title) => setCategoryTitle({ title, categoryId: category.id })}
              onReorderUp={() =>
                reorderCategory({
                  direction: 'up',
                  categoryId: category.id,
                  categoryIndex: i,
                })
              }
              onReorderDown={() =>
                reorderCategory({
                  direction: 'down',
                  categoryId: category.id,
                  categoryIndex: i,
                })
              }
              onRequestDelete={() => deleteCategory({ categoryId: category.id, categoryIndex: i })}
              onRequestRefetch={onRequestRefetch}
              canReorderUp={i !== 0}
              canReorderDown={i !== categories.length - 1}
              key={category.id}
            />
          ))}
        </FlexWrapper>
        <Droppable
          accepts="WIZARD"
          onDropAccepted={(data) => unpublishWizard({ origin: data })}
          className="uncategorized-wrapper"
        >
          <DragState>
            {({ isDragging }) =>
              isDragging && (
                <div className={'unpublish-shade animated fadeIn fast-animation'}>
                  Unpublish Form
                </div>
              )
            }
          </DragState>
          <Category
            filter={filter}
            title="Unpublished Forms"
            id="uncategorized"
            uncategorized
            activeCategory={activeCategory}
            icon={icon}
            canReorderUp={false}
            canReorderDown={false}
            wizards={uncategorizedWizards}
          />
        </Droppable>
        <FabButton ariaLabel="Expand available actions">
          <FabSubButton tooltip="Create New Category" onClick={addCategory}>
            <AddCategoryIcon />
          </FabSubButton>
          <FabSubButton
            tooltip={`Create New ${activeCategory.charAt(0).toUpperCase()}${activeCategory.slice(
              1
            )} Form`}
            onClick={startNewWizard}
          >
            <AddWizardIcon />
          </FabSubButton>
          <FabSubButton tooltip="Import" onClick={() => setImportDialogOpen(true)}>
            <ImportIcon />
          </FabSubButton>
        </FabButton>
        <ImportDialog
          isOpened={importDialogOpen}
          closeModal={closeImportDialog}
          closeAndReload={closeAndReload}
        />
        <DragState>
          {(state) =>
            state.isDragging && (
              <Portal isOpened={true}>
                <React.Fragment>
                  <div
                    className="form-manager-scroll-zone"
                    onMouseEnter={registerScrollUp}
                    onMouseLeave={stopScroll}
                  />
                  <div
                    className="form-manager-scroll-zone bottom"
                    onMouseEnter={registerScrollDown}
                    onMouseLeave={stopScroll}
                  />
                </React.Fragment>
              </Portal>
            )
          }
        </DragState>
      </DragProvider>
    </Body>
  )
}
FormManager = connect(
  (state) => ({
    filter: state.formManager.filter,
  }),
  {
    setCategoryTitle,
    toggleVisibility,
    reorderCategory,
    addCategory,
    deleteCategory,
    unpublishWizard,
  }
)(FormManager)

let Toolbar = ({ selectedIcon, setFilter, setAndPushIcon, filter }) => {
  const [menuAnchor, setMenuAnchor] = React.useState(null)
  const [iconMenuOpen, setIconMenuOpen] = React.useState(false)
  const iconButton = React.useRef()
  const input = React.useRef()

  const openIconMenu = () => {
    setMenuAnchor(iconButton.current)
    setIconMenuOpen(true)
  }
  const closeIconMenu = () => {
    setIconMenuOpen(false)
  }
  const setIcon = ({ value }) => {
    setAndPushIcon(value)
  }
  const selectInput = () => {
    input.current.select()
  }
  const clearFilter = () => {
    setFilter('')
    input.current.focus()
  }
  return (
    <div className="toolbar">
      <div className="icon-wrapper">
        <div className="selected-icon" ref={iconButton} onClick={openIconMenu}>
          {ICONS[selectedIcon]}
        </div>
        <FloatingMenu
          className="fm-icon-menu"
          isOpen={iconMenuOpen}
          alignTo={menuAnchor}
          align="right"
          onRequestClose={closeIconMenu}
          options={FORM_MANAGER_ICON_KEYS.map(key => ({ value: key, label: ICONS[key], onClick: setIcon }))}
        />
      </div>
      <div className="filter-wrapper">
        <input
          ref={input}
          className="filter"
          type="text"
          placeholder="Filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          onFocus={selectInput}
          aria-label="Filter Forms"
        />
        {filter && (
          <button aria-label="clear filter" className="clear" onClick={clearFilter}>
            <CrossIcon />
          </button>
        )}
      </div>
    </div>
  )
}
Toolbar = connect(
  (state) => ({
    filter: state.formManager.filter,
  }),
  { setAndPushIcon, setFilter }
)(Toolbar)
